import React, { ChangeEvent, FocusEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import useFieldSize from 'hooks/useFieldSize';
import { validateEmail } from 'shared';

import { CommonBaseProps, InputProps, ValidateProp } from './types';
import EmailInlineTag from '../EmailInlineTag';
import Error from '../Error';
import { DS_CLASS_NAME, FIELD_CONFIG } from '../constants';
import useUniqueId from '../helpers/useUniqueId';
import { FieldSize } from '../types';

export interface EmailPillInputProps extends CommonBaseProps, InputProps, ValidateProp {
  values: string[];
  onChange: (params: { event: ChangeEvent<HTMLInputElement>; values: string[] }) => void;
  onBlur?: (params: { event: FocusEvent<HTMLInputElement>; values: string[] }) => void;
  placeholder?: string;
  size?: FieldSize;
  error?: string;
  showErrorMessage?: boolean;
  hideLabel?: boolean;
  bigLabel?: string;
  name?: string;
}

const EmailPillInput: React.FC<EmailPillInputProps> = ({
  values = [],
  onChange,
  onBlur,
  placeholder = '',
  label,
  disabled = false,
  required = false,
  size,
  error,
  showErrorMessage = true,
  hideLabel = false,
  bigLabel,
  name = '',
  autoFocus = false,
  tabIndex,
  onValidate
}) => {
  const id = useUniqueId();
  const scaledSize = useFieldSize();
  const fieldSize = size || scaledSize;
  const [inputValue, setInputValue] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [inputWidth, setInputWidth] = useState<number>(120);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mirrorRef = useRef<HTMLSpanElement>(null);

  const hasError = !!error || !!errorMessage;
  const hasValue = values.length > 0 || !!inputValue;

  useEffect(() => {
    if (onValidate) {
      const validationResult = validateEmail(inputValue, false);
      onValidate(validationResult);
    }
  }, [inputValue, !!onValidate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update input width based on mirror span
  useEffect(() => {
    if (mirrorRef.current && inputRef.current) {
      // Copy input value to mirror, replacing spaces with non-breaking spaces
      mirrorRef.current.textContent = inputValue.replace(/\s/g, '\u00a0') || ' ';
      // Set input width based on mirror width, with minimum width
      const width = Math.max(mirrorRef.current.offsetWidth + 2, 120);
      setInputWidth(width);
    }
  }, [inputValue]);

  const validateAndAddEmail = (email: string): boolean => {
    const trimmed = email.trim();

    if (!trimmed) {
      if (required && values.length === 0) {
        setErrorMessage('Email address is required.');
        return false;
      }
      return false;
    }

    const validation = validateEmail(trimmed, false);
    if (!validation.valid) {
      setErrorMessage(validation.error);
      return false;
    }

    // Check for duplicates
    if (values.some(v => v.toLowerCase() === trimmed.toLowerCase())) {
      setErrorMessage('This email is already added.');
      return false;
    }

    // Add email
    const newValues = [...values, trimmed];
    const syntheticEvent = {
      target: { value: trimmed }
    } as ChangeEvent<HTMLInputElement>;

    onChange({ event: syntheticEvent, values: newValues });
    setInputValue('');
    setErrorMessage(undefined);
    return true;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // Clear error when user starts typing
    if (errorMessage) {
      setErrorMessage(undefined);
    }

    // Don't call onChange here - we only call it when an email is added
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const success = validateAndAddEmail(inputValue);

      if (success && e.key === 'Tab') {
        // Blur on Tab after successful add
        inputRef.current?.blur();
      }
    } else if (e.key === 'Backspace' && !inputValue && values.length > 0) {
      // Remove last pill if input is empty and backspace is pressed
      const newValues = values.slice(0, -1);
      const syntheticEvent = {
        target: { value: '' }
      } as ChangeEvent<HTMLInputElement>;

      onChange({ event: syntheticEvent, values: newValues });
    }
  };

  const handleBlur = (_e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);

    // Try to add email on blur if there's input
    if (inputValue.trim()) {
      validateAndAddEmail(inputValue);
    }

    if (onBlur) {
      const syntheticEvent = {
        target: { value: inputValue }
      } as FocusEvent<HTMLInputElement>;

      onBlur({ event: syntheticEvent, values });
    }
  };

  const handleFocus = (_e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    const newValues = values.filter(v => v.toLowerCase() !== emailToRemove.toLowerCase());
    const syntheticEvent = {
      target: { value: '' }
    } as ChangeEvent<HTMLInputElement>;

    onChange({ event: syntheticEvent, values: newValues });
    setErrorMessage(undefined);

    // Focus input after removal
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const fieldConfig = FIELD_CONFIG[fieldSize];
  const paddingX = fieldConfig.fieldPaddingX;
  const minHeight = fieldConfig.height;

  // Border styling to match Input component
  const borderWidth = hasError ? 2 : isFocused ? 2 : 1;
  const borderColor = hasError ? '#e02d3c' : isFocused ? '#0078ff' : '#eceff1'; // red-700, blue-500, gray-50

  return (
    <div className={classNames(DS_CLASS_NAME, 'Input-outerContainer', `is-size-${fieldSize}`)}>
      <div className="Input-innerContainer">
        <div
          ref={containerRef}
          className={classNames(DS_CLASS_NAME, 'Input', `is-size-${fieldSize}`, 'EmailPillInput-container', {
            'has-error': hasError,
            'is-disabled': disabled,
            'has-value': hasValue,
            'is-focused': isFocused
          })}
          style={{
            minHeight,
            height: 'auto',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            alignContent: 'center',
            gap: 8,
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 16,
            paddingRight: paddingX,
            border: `${borderWidth}px solid ${borderColor}`,
            borderRadius: 8,
            backgroundColor: disabled ? '#f4f6f9' : '#ffffff', // gray-25 when disabled, white otherwise
            cursor: 'text',
            position: 'relative',
            boxSizing: 'border-box',
            width: '100%'
          }}
          onClick={e => {
            // If clicking on the container (not on a pill), focus the input
            if (
              e.target === containerRef.current ||
              (e.target as HTMLElement).classList.contains('EmailPillInput-container')
            ) {
              inputRef.current?.focus();
            }
          }}
        >
          {/* Render pills */}
          {values.map((email, index) => (
            <EmailInlineTag key={`${email}-${index}`} value={email} onRemove={() => handleRemoveEmail(email)} />
          ))}

          {/* Hidden mirror span for width measurement */}
          <span
            ref={mirrorRef}
            aria-hidden="true"
            className={classNames('Text', 'is-body-md', DS_CLASS_NAME)}
            style={{
              visibility: 'hidden',
              whiteSpace: 'nowrap',
              position: 'absolute',
              padding: 0,
              margin: 0,
              height: 'auto',
              width: 'auto'
            }}
          />

          {/* Input field - inline with pills */}
          <input
            id={id}
            ref={inputRef}
            name={name}
            className={classNames(DS_CLASS_NAME, 'Input-input', 'EmailPillInput-input', 'Text', 'is-body-md', {
              'has-error': hasError,
              'is-disabled': disabled,
              'is-focused': isFocused,
              'has-value': hasValue,
              'has-small-label': !hideLabel
            })}
            value={inputValue}
            placeholder={values.length === 0 ? placeholder : ''}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onClick={e => {
              // Ensure input gets focus when clicking in the input area
              e.currentTarget.focus();
            }}
            disabled={disabled}
            autoFocus={autoFocus}
            tabIndex={tabIndex}
            style={{
              flex: '0 0 auto',
              width: inputWidth,
              minWidth: 120,
              outline: 'none',
              border: 'none',
              background: 'transparent',
              padding: 0,
              margin: 0,
              fontSize: 'inherit',
              fontFamily: 'inherit',
              color: 'inherit',
              height: 32,
              lineHeight: '32px',
              cursor: 'text',
              position: 'relative',
              zIndex: 1
            }}
            aria-label={hideLabel ? label : undefined}
          />
          {!hideLabel && (
            <label className={classNames('Input-label')} htmlFor={id}>
              {!hasValue && !isFocused ? bigLabel || label : label}
              {required ? ' *' : ''}
            </label>
          )}
        </div>
        {(!!error || !!errorMessage) && showErrorMessage && (
          <Error error={error || errorMessage} inputSize={fieldSize} />
        )}
      </div>
    </div>
  );
};

export default EmailPillInput;
